'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Mail, MapPin, Phone, X } from 'lucide-react';
import {
  REGISTRY_BLOCK,
  parseUtilityDismissible,
  parseUtilityRotateMs,
} from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import { resolveCtaLucideByIconKey } from '../../icons/ctaLucideMap';
import { SocialIcon } from '../../icons/SocialIcon';
import { pickBlockField } from '../../lib/localized';
import {
  resolveUtilityBarItems,
  type ResolvedUtilityItem,
} from '../../lib/resolveUtilityBarItems';
import { pickChrome } from '../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../types';

type Arrangement = 'static' | 'sticky' | 'carousel' | 'collapse';

/** Phone chrome: icon wins; else label; else omit. */
type UtilityFace = 'icon' | 'label';

function resolveArrangement(registryBlockKey?: string): Arrangement {
  if (registryBlockKey === REGISTRY_BLOCK.presentation.utilityBarStatic) return 'static';
  if (registryBlockKey === REGISTRY_BLOCK.presentation.utilityBarCarousel) return 'carousel';
  if (registryBlockKey === REGISTRY_BLOCK.presentation.utilityBarCollapse) return 'collapse';
  return 'sticky';
}

function dismissStorageKey(blockId: string): string {
  return `ws-utility-dismissed:${blockId}`;
}

function defaultIconFor(item: ResolvedUtilityItem) {
  if (item.kind === 'phone') return Phone;
  if (item.kind === 'email') return Mail;
  if (item.kind === 'address') return MapPin;
  return Phone;
}

function resolveUtilityFace(item: ResolvedUtilityItem): UtilityFace | null {
  if (item.kind === 'social') {
    return 'icon';
  }
  const hasIcon = Boolean(
    resolveCtaLucideByIconKey(item.icon) || item.icon?.trim() || defaultIconFor(item),
  );
  const hasLabel = Boolean(item.label?.trim());
  if (hasIcon) return 'icon';
  if (hasLabel) return 'label';
  return null;
}

function UtilityItemView({
  item,
  face,
  editable,
  activeEditTarget,
}: {
  item: ResolvedUtilityItem;
  face: UtilityFace;
  editable?: boolean;
  activeEditTarget: BlockRenderProps['activeEditTarget'];
}) {
  const faceClass =
    face === 'icon' ? 'ws-utility__item--icon' : 'ws-utility__item--label-only';

  if (item.kind === 'social') {
    const body = (
      <>
        <SocialIcon icon={item.network} size="sm" className="ws-utility__icon" />
        <span className="ws-utility__text">{item.label}</span>
      </>
    );
    if (editable) {
      return (
        <span
          className={`ws-utility__item ws-utility__item--social ${faceClass}`}
          data-utility-kind="social"
          data-utility-face={face}
        >
          {body}
        </span>
      );
    }
    return (
      <a
        className={`ws-utility__item ws-utility__item--social ${faceClass}`}
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={item.label}
        data-utility-kind="social"
        data-utility-face={face}
      >
        {body}
      </a>
    );
  }

  const lucide = resolveCtaLucideByIconKey(item.icon) ?? defaultIconFor(item);
  const showIcon = face === 'icon';
  const body = (
    <>
      {showIcon ? (
        <EditableContent
          as="span"
          fieldKey="utilityItems"
          editable={editable}
          activeEditTarget={activeEditTarget}
          className="ws-utility__icon-hit"
        >
          <LucideIcon icon={lucide} size="sm" className="ws-utility__icon" />
        </EditableContent>
      ) : null}
      <span className="ws-utility__text">{item.label}</span>
    </>
  );

  if (item.kind === 'address' || editable) {
    return (
      <span
        className={`ws-utility__item ${faceClass}`}
        data-utility-kind={item.kind}
        data-utility-face={face}
      >
        {body}
      </span>
    );
  }

  return (
    <a
      className={`ws-utility__item ${faceClass}`}
      href={item.href}
      aria-label={item.label}
      data-utility-kind={item.kind}
      data-utility-face={face}
    >
      {body}
    </a>
  );
}

/**
 * Global chrome — thin utility strip above navigation.
 * Live site omits when no items resolve; studio edit shows a ghost affordance.
 */
export function UtilityBar({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const arrangement = resolveArrangement(block.registryBlockKey);
  const items = resolveUtilityBarItems(block, locale);
  const dismissible = parseUtilityDismissible(
    pickBlockField(block.content, 'dismissible', locale),
  );
  const rotateMs = parseUtilityRotateMs(pickBlockField(block.content, 'rotateMs', locale));
  const regionLabel = pickChrome('utilityBarRegion', locale);
  const dismissLabel = pickChrome('utilityBarDismiss', locale);
  const expandLabel = pickChrome('utilityBarExpand', locale);
  const collapseLabel = pickChrome('utilityBarCollapse', locale);
  const emptyLabel = pickChrome('utilityBarEmpty', locale);
  const isDraftHidden = block.hidden === true;
  const facedItems = items
    .map((item) => {
      const face = resolveUtilityFace(item);
      return face ? { item, face } : null;
    })
    .filter((row): row is { item: ResolvedUtilityItem; face: UtilityFace } => row != null);
  const isEmpty = facedItems.length === 0;
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [expanded, setExpanded] = useState(Boolean(editable && isActive));
  const target =
    editable && activeEditTarget ? activeEditTarget : null;

  /** Publish band height so absolute Capsule / Overlay clear the strip (no hamburger clip). */
  useEffect(() => {
    const root = rootRef.current;
    const site = root?.closest('.ws-site') as HTMLElement | null;
    if (!root || !site) return;

    const sync = () => {
      if (root.hasAttribute('hidden') || root.offsetParent === null) {
        site.style.setProperty('--ws-utility-band', '0px');
        return;
      }
      const height = Math.ceil(root.getBoundingClientRect().height);
      site.style.setProperty('--ws-utility-band', `${Math.max(0, height)}px`);
    };
    sync();
    requestAnimationFrame(() => {
      requestAnimationFrame(sync);
    });
    const ro = new ResizeObserver(sync);
    ro.observe(root);
    return () => {
      ro.disconnect();
      site.style.removeProperty('--ws-utility-band');
    };
  }, [arrangement, dismissed, expanded, isEmpty, facedItems.length, isDraftHidden]);

  useEffect(() => {
    if (editable || !dismissible) return;
    try {
      if (sessionStorage.getItem(dismissStorageKey(block.blockId)) === '1') {
        setDismissed(true);
      }
    } catch {
      /* private mode */
    }
  }, [block.blockId, dismissible, editable]);

  useEffect(() => {
    if (editable || arrangement !== 'carousel' || facedItems.length < 2) return;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const timer = window.setInterval(() => {
      setCarouselIndex((index) => (index + 1) % facedItems.length);
    }, rotateMs);
    return () => window.clearInterval(timer);
  }, [arrangement, editable, facedItems.length, rotateMs]);

  useEffect(() => {
    if (arrangement !== 'collapse') return;
    setExpanded(Boolean(editable && isActive));
  }, [arrangement, editable, isActive]);

  /* Live / bake omit empty, dismissed, or draft-hidden. Studio keeps a muted ghost. */
  if (!editable && (isEmpty || dismissed || isDraftHidden)) {
    return null;
  }

  const visibleItems =
    arrangement === 'carousel' && facedItems.length > 0
      ? [facedItems[Math.min(carouselIndex, facedItems.length - 1)]!]
      : facedItems;

  const onDismiss = () => {
    if (editable) return;
    setDismissed(true);
    try {
      sessionStorage.setItem(dismissStorageKey(block.blockId), '1');
    } catch {
      /* private mode */
    }
  };

  const collapsed = arrangement === 'collapse' && !expanded && !editable;

  return (
    <div
      ref={rootRef}
      className={[
        'ws-utility',
        `ws-utility--${arrangement}`,
        isActive ? 'ws-block--active' : '',
        isDraftHidden ? 'ws-block--hidden' : '',
        isEmpty ? 'ws-utility--empty' : '',
        collapsed ? 'ws-utility--collapsed' : '',
        expanded ? 'ws-utility--expanded' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-utility-arrangement={arrangement}
      data-utility-dismissible={dismissible ? 'true' : 'false'}
      data-utility-rotate-ms={String(rotateMs)}
      data-ws-utility=""
      {...(isDraftHidden ? ({ 'data-block-hidden': 'true' } as const) : {})}
      role="region"
      aria-label={regionLabel}
    >
      {arrangement === 'collapse' ? (
        <button
          type="button"
          className="ws-utility__teaser"
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={expanded ? collapseLabel : expandLabel}
          onClick={() => {
            if (editable) return;
            setExpanded((value) => !value);
          }}
        >
          <LucideIcon icon={ChevronDown} size="sm" className="ws-utility__teaser-icon" />
        </button>
      ) : null}

      <div className="ws-utility__inner" id={panelId} hidden={collapsed || undefined}>
        {isEmpty ? (
          <EditableContent
            as="button"
            type="button"
            fieldKey="utilityItems"
            editable={editable}
            activeEditTarget={target}
            className="ws-utility__empty"
          >
            {emptyLabel}
          </EditableContent>
        ) : (
          <ul className="ws-utility__list" data-ws-utility-list>
            {visibleItems.map(({ item, face }) => (
              <li key={item.id} data-ws-utility-slide={item.id}>
                <UtilityItemView
                  item={item}
                  face={face}
                  editable={editable}
                  activeEditTarget={target}
                />
              </li>
            ))}
          </ul>
        )}

        {dismissible ? (
          <button
            type="button"
            className="ws-utility__dismiss"
            aria-label={dismissLabel}
            data-ws-utility-dismiss
            onClick={onDismiss}
          >
            <LucideIcon icon={X} size="sm" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
